import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Modal } from '@/components/kit';
import apiClient from '@/services/apiClient';
import { money } from '../lib/scope';
import { useWallet, walletRunwayWeeks } from '../lib/useManagerData';

/**
 * AUTO TOP-UP, ON THE MANAGER'S OWN SURFACE.
 *
 * The board's wallet row ends in "Auto top-up ›". The only place the product
 * puts that control today is `WalletCard`, half-way down `/admin/subscription`
 * — a MAIE plan chooser with seats and a term on it. Sending a manager there
 * from a row about the message wallet is the §8 failure of a control that
 * "navigates somewhere unrelated", so the control is built here instead,
 * against the endpoints that already exist:
 *
 *   GET /me/wallet              balance, auto-reload config, 25-row ledger
 *   PUT /me/wallet/auto-reload  enabled / threshold / amount  (billing:write)
 *
 * WHEN IT IS DISABLED IT SAYS WHY. `WalletBillingService` is explicit that
 * auto-reload is Stripe-only and inert without a saved card — "it fires, fails
 * and emails the owner" — so the switch is refused with the reason rather than
 * offered and silently ignored. FUNDING the wallet is a payment flow and stays
 * where the payment flow lives; this screen says so and does not pretend to
 * carry it.
 */
export function AutoTopUpModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const { wallet, isLoading } = useWallet();
  const [threshold, setThreshold] = useState('');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);

  const supported = wallet?.autoReload.supported ?? false;
  const cardOnFile = wallet?.autoReload.cardOnFile ?? false;
  const enabled = wallet?.autoReload.enabled ?? false;
  const canSwitchOn = supported && cardOnFile;

  const thresholdMajor = wallet?.autoReload.thresholdMinor != null
    ? String(wallet.autoReload.thresholdMinor / 100) : '';
  const amountMajor = wallet?.autoReload.reloadAmountMinor != null
    ? String(wallet.autoReload.reloadAmountMinor / 100) : '';

  const save = useMutation({
    mutationFn: async (next: boolean) => {
      const body: Record<string, unknown> = { enabled: next };
      const t = Number(threshold || thresholdMajor);
      const a = Number(amount || amountMajor);
      if (next) {
        if (!Number.isFinite(t) || t <= 0 || !Number.isFinite(a) || a <= 0) {
          throw new Error('A threshold and a reload amount are both needed.');
        }
        body.thresholdMinor = Math.round(t * 100);
        body.reloadAmountMinor = Math.round(a * 100);
      }
      await apiClient.put('/me/wallet/auto-reload', body);
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['cm-wallet'] });
      onClose();
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'That did not save.'),
  });

  const weeks = walletRunwayWeeks(wallet);
  const currency = wallet?.currency?.toUpperCase() ?? 'EGP';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Auto top-up"
      sub="so reminders and messages never stop for an empty wallet"
      width={460}
      foot={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Close</button>
          {/* A DISABLED CONTROL MUST LOOK DISABLED. In primary styling it is a
              full-strength purple button that happens not to respond, which is
              the §8 dead control in its worst form — this surface's own
              convention is outline plus the reason in the label. */}
          <button
            type="button"
            className={enabled || canSwitchOn ? 'btn btn-primary' : 'btn btn-outline'}
            disabled={save.isPending || isLoading || (!enabled && !canSwitchOn)}
            title={!enabled && !canSwitchOn
              ? 'Auto top-up needs a card on file with a provider that can charge it off-session'
              : undefined}
            onClick={() => save.mutate(!enabled)}
          >
            {enabled
              ? 'Turn auto top-up off'
              : canSwitchOn
                ? 'Turn auto top-up on'
                : 'Turn auto top-up on — needs a card on file'}
          </button>
        </>
      }
    >
      <Card>
        <p className="t-ovl mut">The wallet now</p>
        <p className="t-sec sec" style={{ marginTop: 4 }}>
          {isLoading ? 'reading…' : money((wallet?.balanceMinor ?? 0) / 100, currency)}
          {weeks !== null ? ` · about ${weeks} week${weeks === 1 ? '' : 's'} left at the current burn`
            : ' · no spend recorded yet, so there is no runway to compute'}
        </p>
        <p className="t-cap mut">
          Auto top-up is {enabled ? 'on' : 'off'}
          {wallet?.autoReload.thresholdMinor != null && wallet.autoReload.reloadAmountMinor != null
            ? ` — it adds ${money(wallet.autoReload.reloadAmountMinor / 100, currency)} whenever the balance falls under ${money(wallet.autoReload.thresholdMinor / 100, currency)}`
            : ''}
        </p>
      </Card>

      {canSwitchOn ? (
        <Card>
          <p className="t-ovl mut">When to reload, and by how much</p>
          <div className="flex" style={{ gap: 10, marginTop: 8 }}>
            <label className="grow">
              <span className="t-cap mut">Top up under ({currency})</span>
              <input
                className="inp"
                inputMode="decimal"
                value={threshold || thresholdMajor}
                onChange={(e) => setThreshold(e.target.value)}
                style={{ width: '100%', background: 'transparent', color: 'var(--ink-900)',
                  border: '1px solid var(--stroke-200)', borderRadius: 'var(--r-sm)',
                  padding: '10px 12px', fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit', minHeight: 44 }}
              />
            </label>
            <label className="grow">
              <span className="t-cap mut">Add ({currency})</span>
              <input
                className="inp"
                inputMode="decimal"
                value={amount || amountMajor}
                onChange={(e) => setAmount(e.target.value)}
                style={{ width: '100%', background: 'transparent', color: 'var(--ink-900)',
                  border: '1px solid var(--stroke-200)', borderRadius: 'var(--r-sm)',
                  padding: '10px 12px', fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit', minHeight: 44 }}
              />
            </label>
          </div>
        </Card>
      ) : (
        <Card>
          <p className="t-ovl mut">It needs a card on file</p>
          <p className="t-sec sec" style={{ marginTop: 4 }}>
            {!supported
              ? 'An automatic reload charges a saved card while nobody is at the screen, and this clinic’s payment method cannot do that.'
              : 'An automatic reload charges the card saved on this clinic’s account, and there is none yet.'}
          </p>
          <p className="t-cap mut mt8">A card is saved under Subscription &amp; Usage.</p>
        </Card>
      )}

      {error ? <p className="t-sec amb" style={{ marginTop: 8 }}>{error}</p> : null}
    </Modal>
  );
}
